import React, { useState } from 'react'
import {ArrowLeft, Star, Calendar, Users, Heart} from 'lucide-react'
import { Link } from 'react-router-dom'
import { RatingSystem } from '../components/RatingSystem'
import { SocialShare } from '../components/SocialShare'
import { CommentSection } from '../components/CommentSection'
import { UserAuth } from '../components/UserAuth'
import { useAuth } from '../context/AuthContext'

export function SmurfsMagicFlute() {
  const { user, toggleFavorite, isFavorite } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const contentId = 'smurfs-magic-flute'
  const favorite = isFavorite(contentId)

  const handleFavoriteClick = () => {
    if (!user) {
      setShowAuthModal(true)
      return
    }
    toggleFavorite(contentId)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-600 mb-8 font-semibold">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <article className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <img 
            src="https://static.lumi.new/87/8717cfaa1cc47f76a788c90416e49295.png" 
            alt="The Smurfs and the Magic Flute"
            className="w-full h-96 object-cover"
          />
          
          <div className="p-8 sm:p-12">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <span className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                RETRO CLASSIC
              </span>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>1976</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4" />
                <span>All Ages</span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-6">
              The Smurfs Magic Flute
            </h1>

            <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
              <p className="text-xl leading-relaxed">
                <strong>The Smurfs and the Magic Flute</strong> is a vintage animated treasure that introduced 
                the beloved blue characters to the big screen. This Belgian-French masterpiece combines 
                musical magic with whimsical storytelling that defined a generation.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">A Timeless Tale</h2>
              <p>
                The story follows a young court entertainer who discovers a magical flute with the power 
                to make anyone dance uncontrollably. When the flute falls into the wrong hands, chaos ensues, 
                and only the tiny Smurfs can save the day. <strong>The Smurfs Magic Flute</strong> adventure 
                showcases the heart and humor that made these characters legendary.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Why It's A Classic</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>First theatrical appearance of the Smurfs</li>
                <li>Beautiful hand-drawn European animation style</li>
                <li>Enchanting musical score and memorable songs</li>
                <li>Timeless themes of friendship and courage</li>
                <li>Perfect introduction to the Smurfs universe</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">The Magic of the Flute</h2>
              <p>
                The magical flute itself is a marvel of storytelling—a seemingly innocent instrument 
                that becomes both blessing and curse. The film explores themes of responsibility and 
                the consequences of wielding magical power, all wrapped in family-friendly adventure 
                that resonates across generations.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Cultural Impact</h2>
              <p>
                Released in 1976, <strong>Smurfs Magic Flute</strong> laid the foundation for the global 
                Smurfs phenomenon. It showcased the charming world of Papa Smurf, Smurfette, and the 
                entire Smurf village to international audiences. The film's success led to the beloved 
                TV series that would captivate children for decades.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Our Verdict</h2>
              <p>
                This is essential viewing for animation historians and families alike. 
                <strong> The Smurfs and the Magic Flute</strong> represents a golden era of European 
                animation and introduces timeless characters in their purest form. The magical elements, 
                combined with the Smurfs' trademark humor, create an unforgettable experience.
              </p>

              <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-2xl p-6 mt-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Quick Facts</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Release Year:</strong> 1976</li>
                  <li><strong>Runtime:</strong> 74 minutes</li>
                  <li><strong>Genre:</strong> Animated Musical, Fantasy</li>
                  <li><strong>Best For:</strong> Retro animation fans, All ages</li>
                </ul>
              </div>
            </div>

            {/* Social Share and Favorite */}
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <button
                onClick={handleFavoriteClick}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 ${
                  favorite
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Heart className={`w-5 h-5 ${favorite ? 'fill-white' : ''}`} />
                {favorite ? 'Favorited' : 'Add to Favorites'}
              </button>
              <SocialShare title="The Smurfs and the Magic Flute" />
            </div>
          </div>
        </article>

        {/* Rating System */}
        <div className="mt-8">
          <RatingSystem contentTitle="The Smurfs and the Magic Flute" />
        </div>

        {/* Comment Section */}
        <div className="mt-8">
          <CommentSection 
            contentId={contentId}
            onAuthRequired={() => setShowAuthModal(true)}
          />
        </div>
      </div>

      {/* Auth Modal */}
      <UserAuth isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  )
}
